import { useState, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { API_URL, GAS_CONVERSION_FACTOR } from "utils/constants";

import { CalculatorContext } from "context/Calculator";
// import { PasswordProtectionContext } from "context/PasswordProtection";

import {
    CANADA_WEATHER_REGION_SELECT_OPTIONS,
    getCitySelectOptionsForRegion,
    getLocationStateFromRowId,
} from "utils/canadaWeatherLocations";
import {
    validateGasBillingPeriod,
    minGasUseForPeriod,
    GAS_PERIOD_MIN_DAYS,
    GAS_PERIOD_MAX_DAYS,
} from "utils/gasBillingPeriod";

import PageLayout from "components/PageLayout";
import Input from "components/Input";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import ConfirmModal from "components/ConfirmModal";
import CollapsableTermsAndConditions from "components/CollapsableTermsAndConditions";
// import PasswordProtection from "components/PasswordProtection";

import "./style.scss";

const GAS_USE_TOTAL_MAX_VALUE_M3 = 10000;
const DHW_MONTHLY_MAX_VALUE_M3 = 150;

const Calculator = () => {
    const { setCalculationResults, calculatorData, setCalculatorData } = useContext(CalculatorContext);
    // const { isPasswordCorrect, passwordError, checkPassword } = useContext(
    //   PasswordProtectionContext);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [confirmSubmitError, setConfirmSubmitError] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAgreeWithTerms, setIsAgreeWithTerms] = useState(false);
    const [areTermsCollapsed, setAreTermsCollapsed] = useState(true);
    const [gasUseTotalMaxValue, setGasUseTotalMaxValue] = useState(GAS_USE_TOTAL_MAX_VALUE_M3);
    const [dhwMonthlyMaxValue, setDhwMonthlyMaxValue] = useState(DHW_MONTHLY_MAX_VALUE_M3);
    // const [errors, setErrors] = useState({});

    const todayYmd = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }, []);

    const gasPeriodValidation = useMemo(
        () => validateGasBillingPeriod(calculatorData.gas_period_start, calculatorData.gas_period_end),
        [calculatorData.gas_period_start, calculatorData.gas_period_end],
    );

    const minGasForPeriod = minGasUseForPeriod(gasPeriodValidation.ok ? gasPeriodValidation.days : GAS_PERIOD_MIN_DAYS);

    // UI may show GJ; API always receives m³.
    const normalized_gasuse_total =
        calculatorData.gasuse_total_units === "GJ"
            ? Number(calculatorData.gasuse_total) * GAS_CONVERSION_FACTOR
            : Number(calculatorData.gasuse_total);

    const normalized_dhw_monthly =
        calculatorData.dhw_monthly_units === "GJ"
            ? Number(calculatorData.dhw_monthly) * GAS_CONVERSION_FACTOR
            : Number(calculatorData.dhw_monthly);

    const buildCalculatePayload = () => {
        const payload = { ...calculatorData };
        // UI-only location fields — never sent; backend works in m³ only for gas volumes.
        delete payload.location_select_value;
        delete payload.location_region;
        delete payload.location_city_id;
        delete payload.gasuse_total;
        // gasuse_total_units / dhw_monthly_units are sent as display prefs and echoed in the response.

        if (payload.dateMode === "Annual") {
            payload.gasuse_annual = normalized_gasuse_total;
            delete payload.start;
            delete payload.end;
            delete payload.gasuse_custom;
        } else {
            payload.start = payload.gas_period_start;
            payload.end = payload.gas_period_end;
            payload.gasuse_custom = normalized_gasuse_total;
            delete payload.year;
            delete payload.gasuse_annual;
        }

        payload.dhw_monthly = normalized_dhw_monthly;

        return payload;
    };

    const citySelectOptions = useMemo(
        () => getCitySelectOptionsForRegion(calculatorData.location_region),
        [calculatorData.location_region],
    );

    const handleInputChange = (name, value) => {
        if (name === "location_region") {
            setCalculatorData((prevState) => ({
                ...prevState,
                location_region: value,
                location_city_id: "",
                location: "",
                normalized_location_name: "",
                location_latitude: null,
                location_longitude: null,
                location_select_value: "",
            }));
            return;
        }

        if (name === "location_city_id") {
            setCalculatorData((prevState) => ({
                ...prevState,
                ...getLocationStateFromRowId(value),
            }));
            return;
        }

        if (name === "year") {
            setCalculatorData((prevState) => ({
                ...prevState,
                year: value,
                gas_period_start: value ? `${value}-01-01` : "",
                gas_period_end: value ? `${value}-12-31` : "",
            }));
            return;
        }

        if (name === "dateMode") {
            setCalculatorData((prevState) => {
                const next = { ...prevState, dateMode: value };
                if (value === "Annual" && prevState.year) {
                    next.gas_period_start = `${prevState.year}-01-01`;
                    next.gas_period_end = `${prevState.year}-12-31`;
                }
                return next;
            });
            return;
        }

        if (name.includes("_units")) {
            // Display-only unit toggle — convert the shown value; API still gets m³.
            const baseFieldName = name.replace("_units", "");
            const currentUnits = calculatorData[name] || "m3";
            const newUnits = value;

            if (newUnits === currentUnits) {
                return;
            }

            const currentValue = Number(calculatorData[baseFieldName]);
            let newValue = currentValue;
            if (currentUnits === "m3" && newUnits === "GJ") {
                newValue = currentValue / GAS_CONVERSION_FACTOR;
            } else if (currentUnits === "GJ" && newUnits === "m3") {
                newValue = currentValue * GAS_CONVERSION_FACTOR;
            }

            setCalculatorData((prevState) => ({
                ...prevState,
                [name]: newUnits,
                [baseFieldName]: Number(newValue.toFixed(2)),
            }));

            if (baseFieldName === "gasuse_total") {
                setGasUseTotalMaxValue(
                    newUnits === "GJ"
                        ? GAS_USE_TOTAL_MAX_VALUE_M3 / GAS_CONVERSION_FACTOR
                        : GAS_USE_TOTAL_MAX_VALUE_M3,
                );
            } else if (baseFieldName === "dhw_monthly") {
                setDhwMonthlyMaxValue(
                    newUnits === "GJ"
                        ? DHW_MONTHLY_MAX_VALUE_M3 / GAS_CONVERSION_FACTOR
                        : DHW_MONTHLY_MAX_VALUE_M3,
                );
            }
            return;
        }


        setCalculatorData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsLoading(true);
        setConfirmSubmitError(null);

        document.body.style.cursor = "progress";

        try {
            const periodCheck = validateGasBillingPeriod(
                calculatorData.gas_period_start,
                calculatorData.gas_period_end,
            );
            if (!periodCheck.ok) {
                setConfirmSubmitError(periodCheck.message);
                setIsLoading(false);
                document.body.style.cursor = "default";
                return;
            }

            const payload = buildCalculatePayload();

            const response = await axios.post(`${API_URL}/calculate`, payload);

            document.body.style.cursor = "default";

            setIsLoading(false);
            const resultId = response.data?.result_id;
            if (!resultId) {
                setConfirmSubmitError("Calculation completed but no result ID was returned. Please try again.");
                return;
            }
            setCalculationResults({
                ...response.data,
                dateMode: response.data.dateMode ?? calculatorData.dateMode,
                year: response.data.year ?? calculatorData.year,
                normalized_location_name:
                    response.data.normalized_location_name ?? calculatorData.normalized_location_name,
                gas_period_start: response.data.gas_period_start ?? calculatorData.gas_period_start,
                gas_period_end: response.data.gas_period_end ?? calculatorData.gas_period_end,
                gasuse_total_units: response.data.gasuse_total_units ?? "m3",
                dhw_monthly_units: response.data.dhw_monthly_units ?? "m3",
            });
            navigate(`/results/${resultId}`);
        } catch (error) {
            console.error(error);
            document.body.style.cursor = "default";

            setIsLoading(false);
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong. Please try again.";
            setConfirmSubmitError(message);
        }
    };

    return (
        <PageLayout
            pageTitle={"ThermalPoint Inputs"}
            pageSubtitle={
                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                    <div style={{ flex: 1 }}>
                        <p>
                            ThermalPoint is a tool to support heat pump sizing and system design using natural gas
                            consumption data. Total gas consumption along with local weather data is used to estimate a
                            home’s heat loss. Based on this estimate, it determines when a heat pump can meet the home’s
                            heating needs on its own and how much of the annual heating demand it can provide. These
                            results help contractors and designers select appropriate heat pump equipment and inform
                            thermostat control strategies.
                        </p>
                        <br></br>
                        {/* <div class="video-container">
                            <h4>Step-by-Step Tutorial</h4>
                            <video class="responsive-video" controls>
                                <source
                                    src="https://sustainabletechnologies.ca/app/uploads/2025/03/ThermalPoint_Walkthrough_720p.mp4"
                                    type="video/mp4"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </div> */}
                    </div>
                </div>
            }
        >
            <form
                noValidate
                onSubmit={(e) => {
                    e.preventDefault();
                    setConfirmSubmitError(null);
                    if (!calculatorData.location_city_id) {
                        return;
                    }
                    if (calculatorData.dateMode === "Annual" && !calculatorData.year) {
                        return;
                    }
                    const periodCheck = validateGasBillingPeriod(
                        calculatorData.gas_period_start,
                        calculatorData.gas_period_end,
                    );
                    if (!periodCheck.ok) {
                        return;
                    }
                    setIsConfirmModalOpen(true);
                }}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2rem",
                }}
            >
                <h1 class="page-layout-subheader">Natural Gas Usage</h1>

                <div class="input-container">
                    <Tooltip
                        tooltipText={
                            <>
                                <b>Annual gas consumption is recommended for the most reliable results. </b>
                                Many utility portals provide an annual total that is quick and easy to access. If using
                                a custom date range, be aware that the gas use shown on a bill may not always represent
                                actual usage for that period. This is because bills can include estimated readings, and
                                later “actual” readings may include adjustments from earlier periods.
                                <p>
                                    For better accuracy, use two actual meter readings (the total values shown on the
                                    gas meter) and calculate gas use as the difference between them. Avoid relying on a
                                    single bill or short time period, as this may result in greater uncertainty.
                                </p>
                            </>
                        }
                    >
                        <label className="input-label">Select Gas Usage Type:</label>
                    </Tooltip>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "8px" }}>
                        <label className="radio-button">
                            <input
                                type="radio"
                                value="Annual"
                                checked={calculatorData.dateMode === "Annual"}
                                onChange={(e) => handleInputChange("dateMode", e.target.value)}
                            />

                            <span className="radio-text">
                                Annual
                                <div className="recommended-text">(Recommended)</div>
                            </span>
                        </label>

                        <label className="radio-button">
                            <input
                                type="radio"
                                value="Custom"
                                checked={calculatorData.dateMode === "Custom"}
                                onChange={(e) => handleInputChange("dateMode", e.target.value)}
                            />
                            <span className="radio-text">Custom Date Range</span>
                        </label>
                    </div>
                </div>

                {calculatorData.dateMode === "Annual" && (
                    <Input
                        label={<>Year:</>}
                        isSelect
                        selectOptions={[
                            { value: "", label: "Select a Year" },
                            { value: "2025", label: "2025" },
                            { value: "2024", label: "2024" },
                            { value: "2023", label: "2023" },
                        ]}
                        type="number"
                        name="year"
                        required
                        onChange={handleInputChange}
                        value={calculatorData.year || ""}
                    />
                )}

                {calculatorData.dateMode === "Custom" && (
                    <section className="gas-billing-period" aria-labelledby="gas-billing-period-heading">
                        <h2 id="gas-billing-period-heading" className="gas-billing-period__title">
                            Custom Natural Gas Billing Period
                        </h2>
                        <p className="gas-billing-period__intro">
                            These dates define the period over which gas consumption was measured. For best results,
                            select a period that represents typical heating behaviour, ideally covering most or all of a
                            heating season.
                            <br></br>
                            <br></br>
                            The start and end dates should correspond to actual (not estimated) meter readings where
                            possible. Short time periods may result in greater uncertainty.
                            <br></br>
                            <br></br>
                            Select the start and end dates that match the gas consumption you are entering (inclusive).
                            The range must be at least {GAS_PERIOD_MIN_DAYS} days and at most {GAS_PERIOD_MAX_DAYS} days
                            (~12 months). The calculator uses this span together with weather data for the same period.
                        </p>
                        <div className="gas-billing-period__fields">
                            <div className="gas-billing-period__field">
                                <Input
                                    label={<>Period start</>}
                                    tooltipText={
                                        <>
                                            First day of the period that your gas meter reading or billing data covers
                                            (inclusive).
                                        </>
                                    }
                                    type="date"
                                    name="gas_period_start"
                                    id="gas_period_start"
                                    required
                                    isToolTipFullWidth
                                    max={
                                        calculatorData.gas_period_end && calculatorData.gas_period_end <= todayYmd
                                            ? calculatorData.gas_period_end
                                            : todayYmd
                                    }
                                    onChange={handleInputChange}
                                    value={calculatorData.gas_period_start || ""}
                                />
                            </div>
                            <div className="gas-billing-period__field">
                                <Input
                                    label={<>Period end</>}
                                    tooltipText={
                                        <>
                                            Last day of the period that your gas meter reading or billing data covers
                                            (inclusive).
                                        </>
                                    }
                                    type="date"
                                    name="gas_period_end"
                                    id="gas_period_end"
                                    required
                                    isToolTipFullWidth
                                    min={calculatorData.gas_period_start || undefined}
                                    max={todayYmd}
                                    onChange={handleInputChange}
                                    value={calculatorData.gas_period_end || ""}
                                />
                            </div>
                        </div>
                        {gasPeriodValidation.ok ? (
                            <p className="gas-billing-period__summary gas-billing-period__summary--ok">
                                Selected period: <strong>{gasPeriodValidation.days}</strong> calendar days (inclusive).
                            </p>
                        ) : (
                            <p className="gas-billing-period__summary gas-billing-period__summary--error" role="alert">
                                {gasPeriodValidation.message}
                            </p>
                        )}
                    </section>
                )}

                <div className="gas-total-fields">
                    <div className="gas-total-fields__field">
                        <Input
                            withSlider
                            label={
                                <span className="input-label">
                                    {calculatorData.dateMode === "Annual"
                                        ? "Total natural gas use over the selected year:"
                                        : "Total natural gas use over the selected period:"}
                                </span>
                            }
                            tooltipText={
                                calculatorData.dateMode === "Annual" ? (
                                    <>
                                        This is the total natural gas consumption for the selected year, including both
                                        space heating and other uses (e.g. hot water, cooking, dryer). The value should
                                        reflect a full year of typical occupancy and heating behaviour.
                                    </>
                                ) : (
                                    <>
                                        This is the total natural gas consumption over the selected time period. Gas
                                        bills typically show both:
                                        <ul>
                                            <li>the meter reading at the start and end of the billing period, and</li>
                                            <li>the calculated usage for that period.</li>
                                        </ul>
                                        The usage value on the bill may not always represent actual consumption for that
                                        period. Even if the end reading is an actual reading, the start reading may have
                                        been estimated, meaning the reported usage can include adjustments from earlier
                                        periods. For best accuracy, calculate gas use as the difference between two
                                        actual meter readings. Values based solely on usage from one bill may be less
                                        reliable.
                                    </>
                                )
                            }
                            type="number"
                            name="gasuse_total"
                            id="gasuse_total"
                            required
                            onChange={handleInputChange}
                            value={calculatorData.gasuse_total}
                            step={calculatorData.gasuse_total_units === "GJ" ? "0.01" : "1"}
                            min={
                                calculatorData.gasuse_total_units === "GJ"
                                    ? Number((minGasForPeriod / GAS_CONVERSION_FACTOR).toFixed(2))
                                    : minGasForPeriod
                            }
                            max={gasUseTotalMaxValue}
                        />
                    </div>
                    <div className="gas-total-fields__field">
                        <Input
                            label={<>Units:</>}
                            isSelect
                            selectOptions={[
                                { value: "m3", label: "Cubic metres (m³)" },
                                { value: "GJ", label: "Gigajoules (GJ)" },
                            ]}
                            type="text"
                            name="gasuse_total_units"
                            required
                            onChange={handleInputChange}
                            value={calculatorData.gasuse_total_units}
                        />
                    </div>
                </div>

                <div className="dhw-monthly-field">
                    <div className="dhw-monthly-field__field">
                        <Input
                            withSlider
                            label={
                                <>
                                    Estimated average <b>monthly</b> natural gas use for purposes other than space
                                    heating (e.g., hot water, stove, dryer, barbeque, etc.):
                                </>
                            }
                            tooltipText={
                                <>
                                    This can be estimated by averaging summer monthly gas use when space heating demand
                                    is minimal. Contractors should also discuss with homeowners how their gas use may
                                    vary seasonally—factors such as extended vacations, changes in occupancy, or hot
                                    water usage patterns can impact the estimate. It is the contractor's responsibility
                                    to assume an accurate value, and if unsure, it is advised to test different values
                                    and assess the impact on the results.
                                </>
                            }
                            type="number"
                            name="dhw_monthly"
                            min={0}
                            max={dhwMonthlyMaxValue}
                            step={calculatorData.dhw_monthly_units === "GJ" ? "0.01" : "1"}
                            required
                            onChange={handleInputChange}
                            value={calculatorData.dhw_monthly}
                        />
                    </div>
                    <div className="dhw-monthly-field__field">
                        <Input
                            label={
                                <>
                                    <br></br>
                                    <br></br>Units:
                                </>
                            }
                            isSelect
                            selectOptions={[
                                { value: "m3", label: "Cubic metres (m³)" },
                                { value: "GJ", label: "Gigajoules (GJ)" },
                            ]}
                            type="text"
                            name="dhw_monthly_units"
                            required
                            onChange={handleInputChange}
                            value={calculatorData.dhw_monthly_units}
                        />
                    </div>
                </div>

                <h1 class="page-layout-subheader">Home Characteristics</h1>
                {/* Home Type, Age, Upgrades, Indoor Setpoint */}
                <Input
                    label={<>Home Type:</>}
                    isSelect
                    selectOptions={[
                        { value: "detached", label: "Detached" },
                        { value: "semi", label: "Semi-Detached / Townhouse End Unit" },
                        { value: "apartment", label: "Townhouse Middle Unit / Apartment" },
                    ]}
                    tooltipText={
                        <>
                            Select the type of dwelling. Homes that share walls typically require less heating than
                            detached homes.
                        </>
                    }
                    type="text"
                    name="home_type"
                    required
                    onChange={handleInputChange}
                    value={calculatorData.home_type}
                />
                <Input
                    label={<>Year Built:</>}
                    isSelect
                    selectOptions={[
                        { value: "pre 1960", label: "Pre 1960s" },
                        { value: "1960 - 1990", label: "1960 - 1990" },
                        { value: "1990 - 2010", label: "1990 - 2010" },
                        { value: "post 2010", label: "Post 2010" },
                    ]}
                    tooltipText={
                        <>The year the home was originally built. Newer homes are generally more energy efficient.</>
                    }
                    type="text"
                    name="home_age"
                    required
                    onChange={handleInputChange}
                    value={calculatorData.home_age}
                />
                <Input
                    label={<>Energy upgrades since original construction:</>}
                    isSelect
                    selectOptions={[
                        { value: "none", label: "None / Minimal Upgrades" },
                        { value: "some", label: "Some Upgrades" },
                        { value: "major", label: "Major Upgrades" },
                    ]}
                    tooltipText={
                        <>
                            Improvements made after the home was built (e.g., insulation, air sealing, or window
                            upgrades).
                            <ul>
                                <li>
                                    Some upgrades: one or two improvements (e.g., attic insulation or window
                                    replacement).
                                </li>
                                <li>
                                    Major upgrades: multiple or more comprehensive improvements (e.g., insulation,
                                    windows, and air sealing upgrades).
                                </li>
                                <li>
                                    For newly constructed homes, select “none” unless significant upgrades were added
                                    beyond standard construction.
                                </li>
                            </ul>
                        </>
                    }
                    type="text"
                    name="energy_upgrades"
                    required
                    onChange={handleInputChange}
                    value={calculatorData.energy_upgrades}
                />
                <Input
                    withSlider
                    label={<>Typical indoor temperature during the heating season (°C)</>}
                    tooltipText={
                        <>
                            This is the typical indoor temperature maintained during the heating season.
                            <p>
                                Lower indoor temperatures reduce heating demand and result in a lower balance point
                                temperature.
                            </p>
                            <p>
                                If the thermostat is set back at certain times (e.g., overnight or during the day),
                                enter the average indoor temperature over a full day. For example, a home kept at 22°C
                                for 16 hours per day and 18°C for 8 hours per day has an average temperature of:
                            </p>
                            <p>(22 × 16 + 18 × 8) ÷ 24 = 20.7°C</p>
                            If unsure, an approximate average is sufficient.
                        </>
                    }
                    type="number"
                    min={15}
                    max={30}
                    name="indoor_temp_heat"
                    onChange={handleInputChange}
                    value={calculatorData.indoor_temp_heat}
                />

                <h1 class="page-layout-subheader">Location</h1>
                <div className="location-fields">
                    <div className="location-fields__field">
                        <Input
                            label={<>Region:</>}
                            isSelect
                            selectOptions={CANADA_WEATHER_REGION_SELECT_OPTIONS}
                            type="text"
                            name="location_region"
                            required
                            isToolTipFullWidth
                            onChange={handleInputChange}
                            value={calculatorData.location_region || ""}
                        />
                    </div>
                    <div className="location-fields__field">
                        <Input
                            label={<>City:</>}
                            tooltipText={
                                <>
                                    If your city is not listed, select the closest available option. This list includes
                                    Canadian cities with sufficient historical weather data from Environment and Climate
                                    Change Canada (ECCC) weather stations.
                                </>
                            }
                            isSelect
                            selectOptions={citySelectOptions}
                            type="text"
                            name="location_city_id"
                            required
                            isToolTipFullWidth
                            disabled={!calculatorData.location_region}
                            onChange={handleInputChange}
                            value={calculatorData.location_city_id || ""}
                        />
                    </div>
                </div>

                <h1 class="page-layout-subheader">Equipment Information</h1>

                <Input
                    withSlider
                    label={<>Annual Fuel Utilization Efficiency (AFUE) of the existing furnace (as a percentage):</>}
                    type="number"
                    name="furnace_eff"
                    step="0.5"
                    min={70}
                    max={98}
                    required
                    onChange={handleInputChange}
                    value={calculatorData.furnace_eff}
                />
                <Input
                    withSlider
                    label={<>Safety margin (as a percentage):</>}
                    tooltipText={
                        <>
                            The safety margin is a multiplier applied to the estimated heat loss, increasing the result
                            to provide a more conservative outcome. A higher value increases the estimated heating
                            requirement and results in a higher (warmer) balance point temperature. Values up to
                            approximately 25% are commonly used. Higher values increase conservatism and reduce the risk
                            of undersizing but may lead to oversizing.
                        </>
                    }
                    type="number"
                    name="derate"
                    min={0}
                    max={150}
                    required
                    onChange={handleInputChange}
                    value={calculatorData.derate}
                />
                <div className="heat-pump-capacities">
                    <Tooltip
                        tooltipText={
                            <>
                                Enter the maximum heat pump capacity at each temperature. For cold-climate air-source
                                heat pumps, this information can typically be found in the{" "}
                                <a href="https://ashp.neep.org/#!/product_list/" target="_blank" rel="noreferrer">
                                    NEEP Heat Pump List
                                </a>
                                . For temperatures below 5°F, ThermalPoint will extrapolate the heat pump capacity based
                                on the 5°F and 17°F inputs. It is the users responsibility to confirm that this is
                                acceptable for their case.
                            </>
                        }
                    >
                        <span className="heat-pump-capacities__heading">
                            Enter Maximum Heat Pump Capacities (in kBTU/hr):
                        </span>
                    </Tooltip>
                    <div className="enter-heat-pump-capacities-container">
                        <Input
                            label={
                                <span style={{ fontSize: "15px", fontWeight: "350" }}>Heat Pump Capacity at 5°F</span>
                            }
                            type="number"
                            name="heatpumpcap_5"
                            min={0}
                            max={100}
                            required
                            onChange={handleInputChange}
                            value={calculatorData.heatpumpcap_5}
                            style={{ width: "150px" }}
                        />
                        <Input
                            label={
                                <span style={{ fontSize: "15px", fontWeight: "350" }}>Heat Pump Capacity at 17°F</span>
                            }
                            type="number"
                            name="heatpumpcap_17"
                            min={0}
                            max={100}
                            required
                            onChange={handleInputChange}
                            value={calculatorData.heatpumpcap_17}
                            style={{ width: "150px" }}
                        />
                        <Input
                            label={
                                <span style={{ fontSize: "15px", fontWeight: "350" }}>Heat Pump Capacity at 47°F</span>
                            }
                            type="number"
                            name="heatpumpcap_47"
                            min={0}
                            max={100}
                            required
                            onChange={handleInputChange}
                            value={calculatorData.heatpumpcap_47}
                            style={{ width: "150px" }}
                        />
                    </div>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                    <Input
                        name="agreeWithTerms"
                        checked={isAgreeWithTerms}
                        type={"checkbox"}
                        onChange={(name, value) => setIsAgreeWithTerms(value)}
                        required
                    />
                    <span>
                        By clicking here, I state that I have read and understood the{" "}
                        <span
                            style={{
                                textDecoration: "underline",
                                fontWeight: "600",
                                cursor: "pointer",
                            }}
                            onClick={() => setAreTermsCollapsed((prevState) => !prevState)}
                        >
                            terms and conditions.
                        </span>
                    </span>
                </div>
                <CollapsableTermsAndConditions isCollapsed={areTermsCollapsed} />
                <Button
                    style={{ width: "fit-content", alignSelf: "center" }}
                    isSubmit={true}
                    disabled={!calculatorData.location_city_id || !isAgreeWithTerms}
                >
                    Calculate
                </Button>
            </form>
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onConfirm={handleSubmit}
                isLoading={isLoading}
                errorMessage={confirmSubmitError}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                    setConfirmSubmitError(null);
                }}
            />
            {/* {!isPasswordCorrect && (
        <PasswordProtection
          passwordError={passwordError}
          checkPassword={checkPassword}
        />
      )} */}
        </PageLayout>
    );
};

export default Calculator;
